import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";
import QRCodeReaderOrdem from "./QRCodeReaderOrdem";

const ROTA_PECA = "ISAGA_ApontamentoSerigrafia/getPeca";
const ROTA_VERIFICAR_LINK = "ISAGA_ApontamentoSerigrafia/verificar-link";
const ROTA_APONTAR = "ISAGA_ApontamentoSerigrafia/apontar";
const ROTA_VALIDAR_MATRICULA = "IAcompanhamentoMontagem/validate-matricula";

const DOMINIO_ESPERADO = "q.agpbrasil.com.br";
const STORAGE_MATRICULA = "listaMontagem_matriculas_SAP";
const TOAST = { duration: 4000 };

const ultimoSegmento = (url) =>
  url.trim().replace(/\/+$/, "").split("/").filter(Boolean).pop() || "";

const interpretarBackOk = (data) => {
  if (data && typeof data === "object") {
    if ("ok" in data || "valido" in data) return Boolean(data.ok ?? data.valido);
    return Boolean(data.existe) && Boolean(data.funcional) && Boolean(data.contemLote);
  }
  return Boolean(data);
};

export const ModalQRSerigrafia = ({ show, onHide }) => {
  const [dadosPeca, setDadosPeca] = useState(null);
  const [verificacao, setVerificacao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [apontando, setApontando] = useState(false);
  const [ordemInput, setOrdemInput] = useState("");
  const [linkInput, setLinkInput] = useState("");

  const [matricula, setMatricula] = useState("");
  const [matriculaSAP, setMatriculaSAP] = useState("");
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [isMatriculaModalOpen, setIsMatriculaModalOpen] = useState(false);
  const [matriculaTemp, setMatriculaTemp] = useState("");
  const [validandoMatricula, setValidandoMatricula] = useState(false);

  const validarMatriculaSAP = useCallback(async (valorMatricula) => {
    const valor = valorMatricula.trim();
    if (!/^\d{6}$/.test(valor)) return null;
    try {
      const { data } = await blogFetch.get(`${ROTA_VALIDAR_MATRICULA}/${encodeURIComponent(valor)}`);
      if (!data?.exists) return null;
      return { nome: data.nome ?? "", matriculaSAP: data.matricula_SAP ?? "" };
    } catch {
      return null;
    }
  }, []);

  const salvarMatriculaStorage = (valor, nome, matriculaSap) => {
    localStorage.setItem(
      STORAGE_MATRICULA,
      JSON.stringify({ principal: valor, nome, matriculaSAP: matriculaSap })
    );
  };

  const limparMatricula = () => {
    localStorage.removeItem(STORAGE_MATRICULA);
    setMatricula("");
    setNomeUsuario("");
    setMatriculaSAP("");
  };

  useEffect(() => {
    if (!show) return;
    const armazenado = localStorage.getItem(STORAGE_MATRICULA);
    if (!armazenado) return;

    let valor = "";
    let nomeSalvo = "";
    let matriculaSAPSalva = "";
    try {
      const obj = JSON.parse(armazenado);
      valor = obj.principal || obj.secundaria || "";
      nomeSalvo = obj.nome || "";
      matriculaSAPSalva = obj.matriculaSAP || "";
    } catch {
      valor = armazenado;
    }
    if (!valor) return;

    setMatricula(valor);
    setNomeUsuario(nomeSalvo);
    setMatriculaSAP(matriculaSAPSalva);

    (async () => {
      const resultado = await validarMatriculaSAP(valor);
      if (resultado === null) {
        limparMatricula();
        toast.error("Matrícula não encontrada no SAP");
      } else {
        setNomeUsuario(resultado.nome);
        setMatriculaSAP(resultado.matriculaSAP);
        salvarMatriculaStorage(valor, resultado.nome, resultado.matriculaSAP);
      }
    })();
  }, [show, validarMatriculaSAP]);

  const handleMatriculaChange = (valor) => setMatriculaTemp(valor.replace(/\D/g, "").slice(0, 6));

  const handleSalvarMatricula = async () => {
    const valor = matriculaTemp.trim();
    if (!/^\d{6}$/.test(valor)) {
      toast.error("A matrícula SAP deve ter 6 dígitos numéricos.");
      return;
    }
    setValidandoMatricula(true);
    const resultado = await validarMatriculaSAP(valor);
    setValidandoMatricula(false);
    if (resultado === null) {
      toast.error("Matrícula não encontrada no SAP");
      return;
    }
    salvarMatriculaStorage(valor, resultado.nome, resultado.matriculaSAP);
    setMatricula(valor);
    setNomeUsuario(resultado.nome);
    setMatriculaSAP(resultado.matriculaSAP);
    setMatriculaTemp("");
    setIsMatriculaModalOpen(false);
    toast.success(`Matrícula registrada: ${resultado.nome}`);
  };

  const resetar = () => {
    setDadosPeca(null);
    setVerificacao(null);
    setCarregando(false);
    setOrdemInput("");
    setLinkInput("");
  };

  const fechar = () => {
    resetar();
    onHide();
  };

  const relerPeca = () => {
    setDadosPeca(null);
    setVerificacao(null);
    setOrdemInput("");
    setLinkInput("");
  };

  const relerLink = () => {
    setVerificacao(null);
    setLinkInput("");
  };

  const isTeclaConfirma = (e) =>
    e.key === "Enter" || e.key === "NumpadEnter" || e.key === "Tab";

  const handleLerPeca = async (conteudo) => {
    const valor = conteudo?.trim();
    if (!valor) return;
    setCarregando(true);
    setVerificacao(null);
    try {
      const { data } = await blogFetch.get(ROTA_PECA, { params: { ordem: valor } });
      if (!data || !data.ordem_Nro_Lote) {
        toast.error("Peça encontrada, mas sem lote retornado pelo servidor.", TOAST);
      }
      setDadosPeca(data);
    } catch (error) {
      console.error(error);
      const msg = error?.response?.status === 404
        ? "Peça não encontrada para o QR lido."
        : (error?.response?.data?.message || "Erro ao buscar a peça no servidor.");
      toast.error(msg, TOAST);
    } finally {
      setCarregando(false);
    }
  };

  const handleApontar = async () => {
    if (apontando) return;
    const numeroOrdem = dadosPeca?.ordem_Serial;
    if (!numeroOrdem) return;
    if (!matricula && !matriculaSAP) {
      toast.error("Registre a matrícula do operador antes de apontar.", TOAST);
      return;
    }
    setApontando(true);
    try {
      const { data } = await blogFetch.post(ROTA_APONTAR, {
        numeroOrdem,
        matriculaSap: matriculaSAP || matricula || "",
      });
      if (data?.success) {
        toast.success(data.mensagem || "Apontamento da serigrafia registrado!", TOAST);
        resetar();
      } else {
        toast.error(data?.mensagem || "Não foi possível apontar.", TOAST);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.mensagem || "Erro ao apontar a serigrafia.", TOAST);
    } finally {
      setApontando(false);
    }
  };

  const handleVerificarLink = async (urlLida) => {
    const url = urlLida?.trim();
    if (!url || !dadosPeca?.ordem_Nro_Lote) return;

    const loteEsperado = String(dadosPeca.ordem_Nro_Lote).trim();
    const loteLido = ultimoSegmento(url);
    const dominioOk = url.toLowerCase().includes(DOMINIO_ESPERADO);
    const loteConfere = loteLido === loteEsperado;
    const frontOk = dominioOk && loteConfere;

    setVerificacao({
      aprovado: frontOk,
      loteConfere,
      loteEsperado,
      loteLido,
      urlLida: url,
    });

    if (frontOk) {
      toast.success("QR verificado com sucesso! Pronto para a próxima peça.", TOAST);
      setTimeout(resetar, 1800);
    } else {
      toast.error("Falha na verificação do QR.", TOAST);
    }

    try {
      const { data } = await blogFetch.get(ROTA_VERIFICAR_LINK, {
        params: { url, lote: loteEsperado, matricula: matriculaSAP || matricula || "" },
        timeout: 6000,
      });
      const backOk = interpretarBackOk(data);
      setVerificacao((prev) =>
        prev && prev.urlLida === url ? { ...prev, aprovado: backOk } : prev
      );
    } catch (error) {
      console.error(error);
    }
  };

  const camposPeca = dadosPeca
    ? [
        ["Ordem", dadosPeca.ordem_Serial],
        ["Descrição do Veículo", dadosPeca.ordem_VehDesc],
        ["Sub Produto", dadosPeca.subProduto_Desc],
        ["Set Atual", dadosPeca.ordem_SetAtual],
      ]
    : [];

  return (
    <>
      <Modal show={show} onHide={fechar} centered backdrop="static" size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold fs-3">Verificar QR — Serigrafia</Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="fs-5">
              <span className="text-muted">Operador: </span>
              {nomeUsuario
                ? <span className="fw-bold">{nomeUsuario} ({matricula})</span>
                : <span className="text-muted fst-italic">não identificado</span>}
            </span>
            <Button
              variant={matricula ? "link" : "warning"}
              className="p-0"
              onClick={() => { setMatriculaTemp(matricula); setIsMatriculaModalOpen(true); }}
            >
              {matricula ? "Trocar" : "Registrar"}
            </Button>
          </div>

          {carregando && (
            <div className="text-center my-3 fs-5">
              <Spinner animation="border" className="me-2" />
              Processando...
            </div>
          )}

          {!dadosPeca ? (
            <>
              <input
                type="text"
                className="form-control form-control-lg mb-2"
                placeholder="Leia ou digite a ordem"
                value={ordemInput}
                autoFocus
                autoComplete="off"
                disabled={carregando}
                onChange={(e) => setOrdemInput(e.target.value)}
                onKeyDown={(e) => {
                  if (isTeclaConfirma(e)) {
                    e.preventDefault();
                    handleLerPeca(ordemInput);
                  }
                }}
              />
              <QRCodeReaderOrdem onLeitura={handleLerPeca} />
            </>
          ) : (
            <div className="border rounded p-3 bg-light mb-3">
              <div className="text-center mb-3">
                <span className="text-muted d-block fs-5">LOTE</span>
                <span className="fw-bold text-primary" style={{ fontSize: "2.6rem", lineHeight: 1 }}>
                  {dadosPeca.ordem_Nro_Lote || "—"}
                </span>
              </div>

              <ul className="list-unstyled mb-3">
                {camposPeca.map(([chave, valor]) => (
                  <li key={chave} className="d-flex justify-content-between border-top py-2 fs-5">
                    <span className="text-muted">{chave}</span>
                    <span className="fw-semibold text-end">{String(valor ?? "")}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="success"
                size="lg"
                className="w-100 fw-bold mb-2"
                onClick={handleApontar}
                disabled={apontando || carregando || (!matricula && !matriculaSAP)}
              >
                {apontando ? "Apontando..." : "Apontar Serigrafia"}
              </Button>
              {!matricula && !matriculaSAP && (
                <p className="text-danger text-center mb-2">Registre a matrícula do operador para apontar.</p>
              )}
              <Button
                variant="outline-primary"
                size="lg"
                className="w-100"
                onClick={relerPeca}
                disabled={apontando || carregando}
              >
                Escanear peça novamente
              </Button>
            </div>
          )}

          {dadosPeca?.ordem_Nro_Lote && (
            <div>
              <h5 className="fw-bold text-secondary mb-2">Verificar QR do link</h5>
              {!verificacao ? (
                <>
                  <input
                    type="text"
                    className="form-control form-control-lg mb-2"
                    placeholder="Leia ou cole o link do QR"
                    value={linkInput}
                    autoFocus
                    autoComplete="off"
                    onChange={(e) => setLinkInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (isTeclaConfirma(e)) {
                        e.preventDefault();
                        handleVerificarLink(linkInput);
                      }
                    }}
                  />
                  <QRCodeReaderOrdem onLeitura={handleVerificarLink} />
                </>
              ) : (
                <div
                  className={`border rounded p-3 ${verificacao.aprovado ? "border-success bg-success bg-opacity-10" : "border-danger bg-danger bg-opacity-10"}`}
                >
                  <div
                    className={`fw-bold text-center mb-3 ${verificacao.aprovado ? "text-success" : "text-danger"}`}
                    style={{ fontSize: "2rem" }}
                  >
                    {verificacao.aprovado ? "✓ QR APROVADO" : "✗ QR REPROVADO"}
                  </div>
                  <div className="fs-5">
                    <div>
                      <span className="text-muted">Lote esperado: </span>
                      <span className="fw-bold text-success">{verificacao.loteEsperado}</span>
                    </div>
                    <div>
                      <span className="text-muted">Lote do link: </span>
                      <span className={`fw-bold ${verificacao.loteConfere ? "text-success" : "text-danger"}`}>
                        {verificacao.loteLido || "—"}
                      </span>
                    </div>
                    <div className="mt-2 text-break">
                      <span className="text-muted">Link: </span>
                      {verificacao.urlLida}
                    </div>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    className="w-100 mt-3"
                    onClick={relerLink}
                    disabled={carregando}
                  >
                    Verificar outro QR do link
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {(dadosPeca || verificacao) && (
            <Button variant="outline-secondary" size="lg" onClick={resetar} disabled={carregando}>
              Limpar tudo
            </Button>
          )}
          <Button variant="secondary" size="lg" onClick={fechar} disabled={carregando}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isMatriculaModalOpen} onHide={() => setIsMatriculaModalOpen(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold fs-4">Registrar Matrícula SAP</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <label className="form-label fw-semibold text-muted">MATRÍCULA SAP (6 dígitos)</label>
          <input
            type="text"
            className="form-control form-control-lg text-primary fw-bold text-center"
            placeholder="Ex: 123456"
            value={matriculaTemp}
            autoFocus
            maxLength={6}
            onChange={(e) => handleMatriculaChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSalvarMatricula(); }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="lg" onClick={() => setIsMatriculaModalOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSalvarMatricula}
            disabled={matriculaTemp.length !== 6 || validandoMatricula}
          >
            {validandoMatricula ? "Validando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalQRSerigrafia;
